import CloseIcon from "@mui/icons-material/Close";
import CloudIcon from "@mui/icons-material/Cloud";
import InputIcon from "@mui/icons-material/Input";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { AxiosError } from "axios";
import React, { Dispatch, DispatchWithoutAction, SetStateAction, useContext, useEffect, useRef } from "react";
import { TypeOptions, toast } from "react-toastify";

import { SessionContext } from "../../Context/SessionContext";
import { UserContext } from "../../Context/UserContext";
import { GemError } from "../../Models/GemError";
import { LeadExternalSystemMap } from "../../Utils/Enums";
import { Error, ErrorBehavior } from "../../Utils/Errors";

export interface HandledRequestError {
  key: Error;
  behavior: ErrorBehavior;
  requestError: AxiosError<GemError>;
  retry: Dispatch<SetStateAction<boolean>>;
  dismiss: Dispatch<SetStateAction<boolean>>;
}

/** The type of the toast action button.
 * @remarks `Zenoti` - The action button opens a new tab to Zenoti.
 * @remarks `Refresh` - The action button reloads the current tab.
 * @remarks `InContact` - The action button opens a new tab to InContact.
 * @remarks `Retry` - The action retries a failed http request.
 */
export type ToastButtonContentType = "Zenoti" | "Refresh" | "InContact" | "Retry";

const toastActionMap: Record<ToastButtonContentType, { icon: React.ReactNode; label: string }> = {
  Zenoti: {
    icon: <InputIcon />,
    label: "Zenoti"
  },
  Refresh: {
    icon: <RefreshIcon />,
    label: "Refresh"
  },
  InContact: {
    icon: <CloudIcon />,
    label: "MAX Updated"
  },
  Retry: {
    icon: <RefreshIcon />,
    label: "Retry"
  }
};

interface ToastContentProps {
  closeButton?: ToastButtonContentType | string;
  message: string;
  showDismissButton: boolean;
  title: string;
  toastStyle: TypeOptions;
  closeToast?: DispatchWithoutAction;
  onClick?: DispatchWithoutAction;
  onDismiss?: DispatchWithoutAction;
}

const ToastContent: React.FC<ToastContentProps> = ({
  closeButton,
  message,
  title,
  toastStyle,
  onClick,
  closeToast,
  showDismissButton,
  onDismiss
}: ToastContentProps) => {
  const handleClick = () => {
    if (onClick) onClick();
    if (closeToast) closeToast();
  };
  const buttonConfig = closeButton ? toastActionMap[closeButton as ToastButtonContentType] : null;
  return (
    <>
      <Stack pl={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body1">{title}</Typography>
          {buttonConfig ? (
            <Tooltip title={buttonConfig.label} placement="right">
              <IconButton color={toastStyle === "default" ? undefined : toastStyle} onClick={handleClick}>
                {buttonConfig.icon}
              </IconButton>
            </Tooltip>
          ) : (
            showDismissButton && (
              <Tooltip title="Dismiss and no longer retry" placement="right">
                <CloseIcon onClick={onDismiss} />
              </Tooltip>
            )
          )}
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Typography variant="caption">{message}</Typography>
        </Stack>
      </Stack>
    </>
  );
};

const handleError = (
  { key, requestError, behavior: { backupMessage, toastStyle, recoveryMessage, actionBtnTitle } }: HandledRequestError,
  title: string,
  action?: () => void,
  dismiss?: () => void
) => {
  const { message } = requestError.response?.data || {};
  const buttonContent = actionBtnTitle || "OK";
  toast(
    <ToastContent
      closeButton={buttonContent}
      message={`${message || backupMessage} ${recoveryMessage}`}
      onClick={action}
      title={title}
      toastStyle={toastStyle}
      showDismissButton={actionBtnTitle === "Retry"}
      onDismiss={dismiss}
    />,
    {
      type: toastStyle,
      autoClose: toastStyle === "info" ? undefined : false,
      closeOnClick: toastStyle === "info",
      closeButton: toastStyle === "info" && !buttonContent,
      toastId: `requestErrorToast_${key}`
    }
  );
};

export const SessionErrorHandler: React.FC = () => {
  const {
    customer,
    lead,
    helpers: { handledError }
  } = useContext(SessionContext);
  const { centers } = useContext(UserContext);

  // keep track of the non user-dismissible errors, dismiss them on session close
  const centersRef = useRef(centers);
  useEffect(() => {
    centersRef.current = centers;
  }, [centers]);
  const customerRef = useRef(customer);
  useEffect(() => {
    customerRef.current = customer;
  }, [customer]);
  const leadRef = useRef(lead);
  useEffect(() => {
    leadRef.current = lead;
  }, [lead]);

  useEffect(() => {
    if (handledError) {
      const leadTypeName = LeadExternalSystemMap[leadRef.current.externalSystem];
      const actionProps = {
        customer: customerRef.current,
        centers: centersRef.current,
        setRetry: handledError.retry,
        setIsDismissed: handledError.dismiss
      };

      // execute the error behavior
      handleError(
        handledError,
        `${leadTypeName}: ${customerRef.current.firstName} ${customerRef.current.lastName}`,
        handledError.behavior.action ? handledError.behavior.action(actionProps) : undefined,
        handledError.behavior?.dismiss ? handledError.behavior.dismiss(actionProps) : undefined
      );
    }
  }, [handledError]);

  return <></>;
};

export const AppErrorHandler: React.FC = () => {
  const { handledError } = useContext(UserContext);

  useEffect(() => {
    if (handledError) {
      const actionProps = {
        setRetry: handledError.retry,
        setIsDismissed: handledError.dismiss
      };

      handleError(
        handledError,
        "GEM Error",
        handledError.behavior?.action ? handledError.behavior.action(actionProps) : undefined,
        handledError.behavior?.dismiss ? handledError.behavior.dismiss(actionProps) : undefined
      );
    }
  }, [handledError]);

  return <></>;
};
